import { useCopyToClipboard } from '@modules/common/hooks';
import { LinkRounded } from '@mui/icons-material';
import {
  Alert,
  Box,
  Stack,
  StackProps,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { TenantOption } from 'generated/graphql';
import pathJoin from 'v2/core/utils/path';

interface TenantSelectionOptionProps {
  tenant: TenantOption;
  onTenantSelection: (slug: string) => void;
  errorMessage?: string;
}

export const TenantSelectionOption = ({
  tenant,
  onTenantSelection,
  errorMessage,
}: TenantSelectionOptionProps) => {
  const copyTextToClipboard = useCopyToClipboard();

  const copyTenantLink = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    copyTextToClipboard(pathJoin(window.location.origin, tenant.slug));
  };

  const getStylesForTenantErrorState = (): StackProps['sx'] => {
    if (!errorMessage) {
      return {};
    }

    return {
      border: '2px solid',
      borderColor: (theme) => alpha(theme.palette.error.main, 0.6),
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    };
  };

  return (
    <Stack>
      <Stack
        width={360}
        borderRadius={2}
        direction="row"
        justifyContent="space-between"
        sx={{
          cursor: 'pointer',
          backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.06),
          ...getStylesForTenantErrorState(),
        }}
        onClick={() => onTenantSelection(tenant.slug)}
      >
        <Stack pl={4} direction="row" spacing={1.5} alignItems="center">
          <Box
            width={30}
            component="img"
            borderRadius={2}
            src={tenant.avatarURL}
          />
          <Typography color="primary" variant="button">
            {tenant.name}
          </Typography>
        </Stack>
        <Tooltip
          title={`Copy direct link. You can use this link to directly sign into ${tenant.name}`}
          disableInteractive
        >
          <Stack
            p={2.5}
            borderRadius={2}
            onClick={(event) => copyTenantLink(event)}
            sx={{
              backgroundColor: (theme) =>
                alpha(theme.palette.text.primary, 0.06),
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }}
          >
            <LinkRounded color="primary" />
          </Stack>
        </Tooltip>
      </Stack>
      {errorMessage && (
        <Alert
          severity="error"
          sx={{
            px: 1.5,
            alignItems: 'center',
            border: '2px solid',
            borderTop: 'none',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderColor: (theme) => alpha(theme.palette.error.main, 0.6),
          }}
        >
          {errorMessage}
        </Alert>
      )}
    </Stack>
  );
};
